import { useState } from "react";
import { Link } from "react-router-dom";
import { useReducedMotion } from "framer-motion";
import { ArrowRight } from "@phosphor-icons/react";
import { Reveal } from "@/components/landing/Reveal";
import { Container, Section } from "@/components/landing/Section";
import { Spine } from "@/components/landing/Spine";
import { PageAtmosphere } from "@/components/landing/PageAtmosphere";
import { GlassFilters } from "@/components/landing/GlassFilters";
import { StickyNav } from "@/components/landing/StickyNav";
import { ParticleField } from "@/components/landing/ParticleField";
import { TaskMarquee } from "@/components/landing/TaskMarquee";
import { TaglineReveal } from "@/components/landing/TaglineReveal";
import { SituationFinder } from "@/components/landing/SituationFinder";
import { Benefits } from "@/components/landing/Benefits";
import { Method } from "@/components/landing/Method";
import { NoCases } from "@/components/landing/NoCases";
import { Risks } from "@/components/landing/Risks";
import { AuthorBlock } from "@/components/landing/AuthorBlock";
import { LeadForm } from "@/components/landing/LeadForm";
import { Faq } from "@/components/landing/Faq";

/* Текст страницы: copy-v1 (copywriter-ru), сверен с CONTEXT.md. Фактов сверх
   CONTEXT.md и прежней версии страницы здесь нет. Кейсов, отзывов и логотипов
   нет сознательно: см. блок «Кейсов пока нет». Вёрстка: единая атмосфера
   (PageAtmosphere) и световая нить (Spine), секции прозрачные, швов между блоками нет. */

const FooterLink = ({ children, ...props }) => (
  <a className="transition-colors duration-300 ease-smooth hover:text-white" {...props}>
    {children}
  </a>
);

export default function Landing() {
  const reduce = useReducedMotion();
  const [needText, setNeedText] = useState("");
  const [needHours, setNeedHours] = useState("");
  const [needRate, setNeedRate] = useState("");

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: reduce ? "auto" : "smooth" });
  };

  return (
    <div className="page-bg relative overflow-x-clip">
      <GlassFilters />
      <PageAtmosphere />
      <div className="relative z-10">
        <StickyNav scrollTo={scrollTo} />
        <main id="main">
          {/* ---------- HERO ---------- */}
          <section id="hero" className="hero-horizon relative isolate flex min-h-[100svh] items-center overflow-x-clip pb-44 pt-28">
            <Container className="grid items-center gap-12 lg:grid-cols-[1.05fr_1fr] lg:gap-10">
              <Reveal>
                <p className="eyebrow">Внедряю ИИ в процессы малого бизнеса</p>
                <h1 className="display mt-5 max-w-[680px] pb-2">
                  <span className="soft">Посчитаю, где ИИ нужен в твоём бизнесе,</span> а где
                  <em className="accent-serif block">хватит порядка</em>
                </h1>
                <p className="mt-6 max-w-[680px] text-lg text-muted">
                  Сначала строю карту процесса и считаю потери в часах и рублях, потом решаю, нужен ли ИИ вообще. Где он
                  не нужен, навожу порядок без него.
                </p>
                <div className="mt-8">
                  <button onClick={() => scrollTo("form")} className="btn group" data-testid="hero-cta">
                    Получить письменный разбор
                    <ArrowRight size={20} weight="bold" className="transition-transform duration-500 ease-smooth group-hover:translate-x-1" />
                  </button>
                  <p className="mt-4 max-w-[680px] text-sm text-muted">
                    Разбор бесплатный, письменный и приходит в течение 2 рабочих дней. Отвечаю я сам, без менеджера.
                  </p>
                </div>
              </Reveal>
              <Reveal delay={0.1}>
                <ParticleField />
              </Reveal>
            </Container>
          </section>

          <TaskMarquee />

          {/* ---------- ПОДБОР ---------- */}
          <Section
            id="podbor"
            title="Покажи, что болит"
            lead="Хаос редко выглядит как хаос. Он выглядит как «у нас так заведено»: заявки по чатам, отчёт копипастом из таблиц, ответ «спроси Марину». Выбери, что ближе, и я покажу, где утекают часы и что с этим делать."
          >
            <SituationFinder
              scrollTo={scrollTo}
              onPick={(label, hours, rate) => {
                setNeedText(label);
                setNeedHours(hours);
                setNeedRate(rate);
              }}
            />
            <p className="mt-10 max-w-[680px] text-base text-muted">
              Если подрядчик уже сделал тебе долго, дорого и не то, начни с письменного разбора. Он бесплатный, и сначала
              ты посмотришь, как я думаю.
            </p>
          </Section>

          {/* ---------- РЕЗУЛЬТАТ ---------- */}
          <Section id="rezultat" title="Что ты получаешь">
            <Benefits />
          </Section>

          <TaglineReveal />

          {/* ---------- МЕТОД ---------- */}
          <Section
            id="metod"
            title="Как это устроено"
            lead="Три шага. Первый бесплатный и ни к чему не обязывает."
          >
            <Method />
          </Section>

          {/* ---------- ДОКАЗАТЕЛЬСТВО ---------- */}
          <Section id="dokazatelstvo" title="Кейсов пока нет">
            <NoCases />
          </Section>

          {/* ---------- РИСК РАЗБОРА (A2 #11): только то, что уже правда ---------- */}
          <Section id="risk" title="Чем ты рискуешь, отправив форму">
            <Risks />
          </Section>

          {/* ---------- FAQ: заголовок закреплён слева, вопросы справа ---------- */}
          <Spine>
            <section id="voprosy" className="py-24 sm:py-32">
              <Container className="grid gap-10 lg:grid-cols-[0.8fr_1.4fr] lg:gap-16">
                <Reveal>
                  <div className="lg:sticky lg:top-28">
                    <h2 className="h2">Частые вопросы</h2>
                    <p className="mt-5 text-lg text-muted">Не нашёл свой? Спроси напрямую, отвечаю сам.</p>
                    <a
                      href="https://t.me/d_evoluer"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-5 inline-flex min-h-10 items-center gap-2 text-base font-medium text-accent-soft transition-colors duration-300 ease-smooth hover:text-white"
                    >
                      Написать в Telegram
                      <ArrowRight size={18} weight="bold" />
                    </a>
                  </div>
                </Reveal>
                <Reveal delay={0.1}>
                  <Faq />
                </Reveal>
              </Container>
            </section>
          </Spine>

          {/* ---------- АВТОР ---------- */}
          <Section id="avtor" title="Кто будет с тобой работать">
            <AuthorBlock />
          </Section>

          {/* ---------- ФОРМА: дуга света возвращается, история замыкается ---------- */}
          <section id="form" className="hero-horizon relative isolate overflow-x-clip pb-56 pt-24 sm:pt-28">
            <Container>
              <Reveal>
                <h2 className="h2 max-w-[760px]">Опиши процесс, пришлю разбор</h2>
                <p className="mt-5 max-w-[680px] text-lg text-muted">
                  Разбор письменный. Созвон будет, только если захочешь после.
                </p>
                <p className="mt-2 max-w-[680px] text-base text-muted">
                  Разбор бесплатный и ни к чему не обязывает. Если проект не работает, скажу прямо и не буду тянуть деньги.
                </p>
              </Reveal>
              <div className="mt-12 max-w-[800px]">
                <LeadForm initialProcess={needText} initialHours={needHours} initialRate={needRate} />
              </div>
            </Container>
          </section>
        </main>

        {/* ---------- ПОДВАЛ ---------- */}
        <footer className="pb-12">
          <Container>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-muted">
              <span className="font-display font-semibold text-white">Gride.pro</span>
              <span>Данил Григорьев</span>
              <FooterLink href="https://t.me/d_evoluer" target="_blank" rel="noopener noreferrer" data-testid="footer-telegram">
                @d_evoluer
              </FooterLink>
              <FooterLink href="mailto:gridepro@yandex.ru" data-testid="footer-email">
                gridepro@yandex.ru
              </FooterLink>
              <Link to="/privacy" className="transition-colors duration-300 ease-smooth hover:text-white" data-testid="footer-privacy">
                Политика обработки данных
              </Link>
              <Link to="/terms" className="transition-colors duration-300 ease-smooth hover:text-white" data-testid="footer-terms">
                Условия использования
              </Link>
              <span>2026</span>
            </div>
            <p className="mt-4 max-w-[560px] text-sm text-muted">
              Разбор бесплатный и ни к чему не обязывает: отправка формы не создаёт договор и не порождает финансовых
              обязательств ни для одной из сторон.
            </p>
          </Container>
        </footer>
      </div>
    </div>
  );
}
